import { z } from 'zod/v3';

import type { OAISchema } from '~/types/openapi';

import { hometaxCardSchema } from './register-form';
import {
  annualRevenueSchema,
  businessOwnerTypeSchema,
  inquiryTypeSchema,
  partnerGradeSchema,
  quoteTypeSchema,
  requesterIndustrySchema,
  serviceTypeSchema,
} from './schemas';

// ============================================================================
// 멤버십 견적 요청 폼 데이터 스키마
// ============================================================================

/**
 * 선택된 파트너 정보 스키마
 * @description 파트너 ID와 이름을 함께 관리
 */
export const targetPartnerSchema = z.object({
  /** 파트너 ID */
  partnerId: z.number(),
  /** 파트너 이름 */
  partnerName: z.string(),
});
export type TargetPartner = z.infer<typeof targetPartnerSchema>;

/**
 * 멤버십 견적 요청 폼 데이터 스키마
 * @description 세무이지 멤버십 견적 요청 폼에서 사용하는 데이터
 * @see POST /api/market-place/partners/quote-requests
 */
export const membershipFormDataSchema = z.object({
  /** 연 매출 구간 (필수) */
  annualRevenue: annualRevenueSchema.or(z.literal('')),

  /** 사업장 주소 (선택) */
  businessAddress: z.string(),

  /** 사업장 상세주소 (선택) */
  businessDetailAddress: z.string(),

  /** 사업자 유형: INDIVIDUAL_GENERAL / INDIVIDUAL_SIMPLIFIED / CORPORATION / OTHER (필수) */
  businessOwnerType: businessOwnerTypeSchema.or(z.literal('')),

  /** 사업장 지역 코드 (필수) */
  businessRegionCode: z.string(),

  /** 사업장 우편번호 (선택) */
  businessZipCode: z.string(),

  /** 홈택스 연동 신용카드 여부 (필수) */
  hasHometaxCard: hometaxCardSchema.or(z.literal('')),

  /** 문의 유형 (필수) */
  inquiryType: inquiryTypeSchema.or(z.literal('')),

  /** 견적 유형 (COMPARE / ONE_TO_ONE / GRADE_BASED) */
  quoteType: quoteTypeSchema.or(z.literal('')),

  /** 요청 내용 (선택) */
  requestContent: z.string(),

  /** 요청자 업종 (필수) */
  requesterIndustry: requesterIndustrySchema.or(z.literal('')),

  /** 요청자 이름 (필수) */
  requesterName: z.string(),

  /** 요청자 연락처 (필수) */
  requesterPhone: z.string(),

  /** 서비스 유형 - TAX 고정 */
  serviceType: serviceTypeSchema,

  /** 대상 파트너 목록 (ID + 이름) */
  targetPartners: z.array(targetPartnerSchema),

  /** 파트너 등급 필터 (GRADE_BASED 견적 시 사용) */
  grade: partnerGradeSchema.optional(),
});
export type MembershipFormData = z.infer<typeof membershipFormDataSchema>;

/**
 * 멤버십 폼 기본값
 */
export const membershipFormDefaultValues: MembershipFormData = {
  // Step 1 (업종, 사업자유형, 문의사항)
  requesterIndustry: '',
  businessOwnerType: '',
  inquiryType: '',
  // Step 2 (주소, 매출규모)
  annualRevenue: '',
  businessAddress: '',
  businessDetailAddress: '',
  businessRegionCode: '',
  businessZipCode: '',
  // Step 3 (비용처리)
  hasHometaxCard: 'Y',
  requestContent: '',
  // Step 4 (연락처)
  requesterName: '',
  requesterPhone: '',
  // 기타
  quoteType: '',
  serviceType: 'TAX',
  targetPartners: [],
  grade: undefined,
};

/**
 * MembershipFormData를 API 요청 형식으로 변환
 * @description 멤버십 폼 데이터를 CreateQuoteRequestRequest로 변환
 */
export function toMembershipQuoteRequest(
  formData: MembershipFormData,
): OAISchema['CreateQuoteRequestRequest'] {
  return {
    annualRevenue: formData.annualRevenue,
    businessAddress: formData.businessAddress || undefined,
    businessDetailAddress: formData.businessDetailAddress || undefined,
    businessOwnerType:
      formData.businessOwnerType as OAISchema['CreateQuoteRequestRequest']['businessOwnerType'],
    businessRegionCode: formData.businessRegionCode,
    businessZipCode: formData.businessZipCode || undefined,
    inquiryType:
      formData.inquiryType as OAISchema['CreateQuoteRequestRequest']['inquiryType'],
    quoteType:
      formData.quoteType as OAISchema['CreateQuoteRequestRequest']['quoteType'],
    requestContent: formData.requestContent || undefined,
    requesterIndustry:
      formData.requesterIndustry as OAISchema['CreateQuoteRequestRequest']['requesterIndustry'],
    requesterName: formData.requesterName,
    requesterPhone: formData.requesterPhone,
    serviceType: formData.serviceType,
    targetPartnerIds: formData.targetPartners.map((p) => p.partnerId),
    grade: formData.grade,
  };
}
